import i18n from 'i18next';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {isTokenValidUseCase, loginUseCase} from '@/compositionRoot';
import type {LoginErrorCode} from '@/domain/useCases/auth/loginUseCase';

export const useController = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {t} = useTranslation();

  const [user, setUsername] = useState('');
  const [pass, setPassword] = useState('');
  const [errors, setErrors] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const loginErrorMessages: Record<LoginErrorCode, string> = {
    INVALID_CREDENTIALS: t('authentication.errors.INVALID_CREDENTIALS'),
    LOGIN_FAILED_REDIRECT: t('authentication.errors.LOGIN_FAILED_REDIRECT'),
    MISSING_CREDENTIALS: t('authentication.errors.MISSING_CREDENTIALS'),
    NETWORK_ERROR: t('authentication.errors.NETWORK_ERROR'),
    UNEXPECTED_ERROR: t('authentication.errors.UNEXPECTED_ERROR'),
    UNKNOWN_ERROR: t('authentication.errors.UNKNOWN_ERROR'),
  };

  const goToDestination = useCallback(() => {
    const redirect = searchParams.get('redirect');
    if (redirect?.startsWith('/admin')) {
      // El admin se monta fuera del router del frontend (basename "/admin"),
      // asi que hace falta un full reload para que src/index.tsx monte AdminRoot.
      window.location.replace(redirect);
      return;
    }
    navigate(`/${i18n.resolvedLanguage}/`);
  }, [navigate, searchParams]);

  useEffect(() => {
    isTokenValidUseCase.execute().then((isValid) => {
      if (isValid) goToDestination();
    });
  }, [goToDestination]);

  const handleLogin = async () => {
    setErrors('');
    setIsLoading(true);

    try {
      const result = await loginUseCase.execute(user, pass);

      if (result?.code) {
        setErrors(loginErrorMessages[result.code]);
      } else {
        // Login exitoso
        goToDestination();
      }
    } catch (error) {
      // Capturar errores no controlados (problemas de red, etc.)
      console.error('Login error:', error);

      // Si el error tiene la estructura {statusCode, payload: {detail}}
      if (error && typeof error === 'object' && 'payload' in error) {
        const errorWithPayload = error as {payload: {detail: string}};
        setErrors(errorWithPayload.payload.detail);
      } else if (error && typeof error === 'object' && 'detail' in error) {
        const errorWithDetail = error as {detail: string};
        setErrors(errorWithDetail.detail);
      } else if (error instanceof Error) {
        setErrors(error.message);
      } else {
        setErrors('Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    user,
    pass,
    errors,
    onUsernameChange: setUsername,
    onPasswordChange: setPassword,
    onLogin: handleLogin,
    isLoading,
  };
};
